"use client";

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * 기능 안내 모달 — 베타 정책 (2026-05-15):
 * 가격/등급 비교 X, 단일 컬럼으로 기능 + 한도만.
 * 베타 종료 후 git history (commit 9cb353c 직전) 에서 가격 표 + CTA 복원.
 */

interface FeatureRow {
  label: string;
  paid: string;
}

const CORE_FEATURES: FeatureRow[] = [
  { label: "① 4단계 라이프사이클", paid: "전부" },
  { label: "② 공급처 요약", paid: "전부" },
  { label: "③ 매장관리", paid: "매입·판매·손익·세금" },
  { label: "④ AI 이미지작업", paid: "10매/월" },
  { label: "⑤ 주변 상권분석 (지도)", paid: "인구·점포·임대·유동인구·휴폐업정보" },
  { label: "⑥ 레시피 분석", paid: "유튜브 영상·재료 정리" },
];
const EXTRA_FEATURES: FeatureRow[] = [
  { label: "AI 비서", paid: "무제한 (실 10,000회/일)" },
  { label: "SNS 자동 글작성", paid: "무제한" },
  { label: "키워드 분석 요청", paid: "무제한" },
  { label: "영수증 OCR (재고 자동)", paid: "무제한" },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function TierInfoModal({ open, onClose }: Props) {
  // ESC 닫기
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-0 sm:px-4"
        >
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-bg-card w-full max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[85vh] flex flex-col border-t sm:border border-border"
          >
            {/* 헤더 */}
            <div className="sticky top-0 bg-bg-card z-10 px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-black text-text">기능 안내</h2>
              <button
                onClick={onClose}
                className="text-text-sub hover:text-text text-2xl leading-none"
                aria-label="닫기"
              >
                ×
              </button>
            </div>

            {/* 본문 — 베타 정책 (2026-05-15): 베타 박스 + 가격/기본 컬럼 + 하단 안내 모두 제거.
                단일 컬럼으로 '기능 + 한도' 만 표시. 베타 종료 후 git revert 로 복원. */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* 기능 표 — 단일 컬럼 (기능명 + 한도) */}
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-bg-elevated">
                      <th className="text-left px-3 py-2 font-bold text-text-sub">기능</th>
                      <th className="text-left px-3 py-2 font-bold text-text-sub">한도</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {CORE_FEATURES.map((f) => (
                      <tr key={f.label}>
                        <td className="px-3 py-2 text-text font-medium">{f.label}</td>
                        <td className="px-3 py-2 text-text font-semibold whitespace-pre-line leading-snug">{f.paid}</td>
                      </tr>
                    ))}
                    <tr className="bg-primary/10 border-t-2 border-primary/20">
                      <td colSpan={2} className="px-3 py-2 text-2xs font-bold text-primary uppercase tracking-wide">
                        ⬇ 부가 기능
                      </td>
                    </tr>
                    {EXTRA_FEATURES.map((f) => (
                      <tr key={f.label} className="bg-bg-elevated/40">
                        <td className="px-3 py-2 text-text font-medium">{f.label}</td>
                        <td className="px-3 py-2 text-text font-semibold whitespace-pre-line leading-snug">{f.paid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 추가 안내 — 커뮤니티 + 프랜차이즈 */}
              <div className="pt-1 space-y-1">
                <p className="text-2xs text-text-sub text-center">
                  💬 커뮤니티 글 작성·댓글은 로그인 후 누구나 가능
                </p>
                <p className="text-2xs text-text-sub text-center">
                  🏢 프랜차이즈 (다매장·API 연동) 는 별도 문의 — arsenium@naver.com
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
